import {$auth} from "@/plugins/auth"
import {roles} from "@/enums/roles"

export type AccountStatus = {
  kind: 'grace' | 'trial'
  title: string
  body: string
  action: string
  /** Whether it can be dismissed. A failed payment cannot. */
  dismissible: boolean
  /** Changes as the deadline nears, so a dismissal does not last forever. */
  key: string
}

type AccountLike = {
  id?: unknown
  in_plus_grace?: unknown
  plus_grace_until?: unknown
  on_plus_trial?: unknown
  trial_days_left?: unknown
} | null | undefined

/** Show the trial countdown only once it is close enough to matter. */
const TRIAL_WARN_FROM = 7

function daysLeft(account: AccountLike): number | null {
  const days = account?.trial_days_left

  return typeof days === 'number' ? days : null
}

function graceDaysLeft(account: AccountLike): number | null {
  const until = account?.plus_grace_until

  if (typeof until !== 'string') return null

  const ms = new Date(until).getTime() - Date.now()

  return ms > 0 ? Math.ceil(ms / 86400000) : 0
}

function plural(days: number): string {
  return days === 1 ? '1 day' : `${days} days`
}

/** Owner only. Nobody else can pay, so nobody else needs telling. */
export function accountStatus(): AccountStatus | null {
  const user = $auth.user

  if (user?.role !== roles.ACCOUNT_OWNER) return null

  const account = user?.account as AccountLike

  if (account?.in_plus_grace === true) {
    const left = graceDaysLeft(account)

    return {
      kind: 'grace',
      title: 'There was a problem with your payment',
      body: left === null
        ? 'Update your payment details to stay on Plus.'
        : `Update your payment details in the next ${plural(left)} to stay on Plus.`,
      action: 'Update payment',
      dismissible: false,
      key: `grace:${account.id}`,
    }
  }

  if (account?.on_plus_trial === true) {
    const left = daysLeft(account)

    if (left === null || left > TRIAL_WARN_FROM) return null

    return {
      kind: 'trial',
      title: left === 0
        ? 'Your Plus trial ends today'
        : `Your Plus trial ends in ${plural(left)}`,
      body: 'Subscribe to keep using Idly’s AI features and added members. '
        + 'If not, you’ll automatically switch to Idly Free when your trial ends.',
      action: 'Subscribe to Plus',
      dismissible: true,
      // Carries the day, so dismissing at a week out still shows tomorrow.
      key: `trial:${account.id}:${left}`,
    }
  }

  return null
}

export function isDismissed(key: string): boolean {
  try {
    return window.localStorage.getItem(`idly.status.${key}`) !== null
  } catch {
    // Private windows throw. Showing the banner beats crashing the shell.
    return false
  }
}

export function dismiss(key: string): void {
  try {
    window.localStorage.setItem(`idly.status.${key}`, '1')
  } catch {
    // They see it again next load, which is survivable.
  }
}
